import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import formService from "services/feedback-service";
import SelectServiceType from "components/Textfields/SelectServiceType";
import SelectOffice from "components/Textfields/SelectOffice";
import SelectDesignation from "components/Textfields/SelectDesignation";
import { object, string } from "yup";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  position: "absolute",
  backgroundColor: "#e9e9e9",
  boxShadow: "3px 2px 20px 3px rgba(0, 0, 0, 0.3)",
  borderRadius: "10px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  p: 2,
  overflow: "auto",
  "@media (min-width: 10px)": {
    height: "90vh",
    width: "85vw",
  },
  "@media (min-width: 480px)": {
    height: "85vh",
    width: "80vw",
  },
  "@media (min-width: 640px)": {
    height: "75vh",
    width: "70vw",
  },
  "@media (min-width: 768px)": {
    height: "75vh",
    width: "70vw",
  },
  "@media (min-width: 1024px)": {
    height: "80vh",
    width: "70vw",
  },
  "@media (min-width: 1082px)": {
    height: "80vh",
    width: "65vw",
  },
};

export default function UpdateServiceModal({
  open,
  handleClose,
  data,
  updateTableFunction,
}) {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formik = useFormik({
    initialValues: {
      title: data?.title || "",
      designationId: data?.designationId || "",
      relatedOfficeId: data?.relatedOfficeId || "",
      serviceKindId: data?.serviceKindId || "",
    },
    validationSchema: object().shape({
      designationId: string().required("Required"),
      relatedOfficeId: string().required("Required"),
      serviceKindId: string().required("Required"),
      title: string().required("Required"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      setError("");

      formService
        .updateService(data.id, values)
        .then(() => {
          alert("Service Updated");
          updateTableFunction();
          handleClose();
        })
        .catch((err) => {
          setError(err?.response?.data?.error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  useEffect(() => {
    if (data) {
      const initialValues = {
        title: data?.title || "",
        designationId: data?.designationId || "",
        relatedOfficeId: data?.relatedOfficeId || "",
        serviceKindId: data?.serviceKindId || "",
      };
      formik.setValues(initialValues);
    }
  }, [data]);

  useEffect(() => {
    const requiredFields = formik.values;
    const areAllValuesFilled = Object.values(requiredFields).every(Boolean);
    setDisabled(!areAllValuesFilled);
  }, [formik.values]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        variant="form"
        component="form"
        onSubmit={formik.handleSubmit}
        autoComplete="off"
        sx={style}
      >
        <Box
          sx={{
            position: "absolute",
            right: 20,
            cursor: "pointer",
            zIndex: 100,
          }}
        >
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CancelIcon />
          </IconButton>
        </Box>
        <Box>
          <Box
            sx={{
              backgroundColor: "#564ee2",
              borderRadius: "10px",
              width: "70%",
              p: 2,
              mb: 2,
              ml: 4,
            }}
          >
            <Typography
              sx={{
                ml: 3,
                fontSize: 25,
                fontWeight: "500",
                color: "#fff",
                fontFamily: "Poppins",
              }}
            >
              UPDATE SERVICE
            </Typography>
          </Box>
          {error}
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 1)",
              boxShadow: "8px 8px 15px 3px rgba(0, 0, 0, 0.3)",
              borderRadius: "10px",
              p: 4,
              mx: 4,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="title"
                  label="Service Name"
                  size="small"
                  disabled={loading}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  variant="outlined"
                  sx={{ pr: 5 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <SelectServiceType
                  label={`Service Type ${
                    formik.values.serviceKindId ? "" : "*"
                  }`}
                  name="serviceType"
                  value={formik.values.serviceKindId}
                  onChange={(fieldName, selectedValue) => {
                    formik.setFieldValue("serviceKindId", selectedValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.serviceKindId &&
                    Boolean(formik.errors.serviceKindId)
                  }
                  helperText={
                    formik.touched.serviceKindId && formik.errors.serviceKindId
                  }
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectDesignation
                  label={`Office ${formik.values.designationId ? "" : "*"}`}
                  name="designationId"
                  value={formik.values.designationId}
                  onChange={(fieldName, selectedValue) => {
                    formik.setFieldValue("designationId", selectedValue);
                  }}
                  subId={formik.values.designationId}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.designationId &&
                    Boolean(formik.errors.designationId)
                  }
                  helperText={
                    formik.touched.designationId && formik.errors.designationId
                  }
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectOffice
                  label={`Office ${formik.values.relatedOfficeId ? "" : "*"}`}
                  name="relatedOfficeId"
                  value={formik.values.relatedOfficeId}
                  onChange={(fieldName, selectedValue) => {
                    formik.setFieldValue("relatedOfficeId", selectedValue);
                  }}
                  subId={formik.values.relatedOfficeId}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.relatedOfficeId &&
                    Boolean(formik.errors.relatedOfficeId)
                  }
                  helperText={
                    formik.touched.relatedOfficeId &&
                    formik.errors.relatedOfficeId
                  }
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            p: 2,
            zIndex: 10,
          }}
        >
          <Button
            disabled={disabled}
            type="submit"
            sx={{
              fontFamily: "Poppins",
              display: "flex",
              alignItems: "center",
              backgroundColor: "#564ee2",
              color: "white",
              py: 1,
              width: "10vw",
              minWidth: "100px",
              "&:hover": {
                color: "black",
                backgroundColor: "#11edd2",
              },
            }}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
